import { BINDING_HEADER, browserAuthPath } from '@/lib/guest-auth/constants';

// WORKAROUND: Keep a partitioned binding copy for embedded browsers that drop the cookie.
// The chat already receives the bearer; remove this fallback if authentication moves entirely server-side.
function bindingKey(tenant: string): string {
  return `bazak-guest-auth-binding:${tenant}`;
}

function rememberBinding({ tenant, binding }: { tenant: string; binding: string }): void {
  try {
    localStorage.setItem(bindingKey(tenant), binding);
  } catch {
    // Storage refused: the cookie is the only carrier here.
  }
}

// WORKAROUND: Send the stored binding when embedded browsers drop cookies.
// Remove with rememberBinding and the readBinding header fallback when auth is entirely server-side.
function bindingHeaders(tenant: string): Record<string, string> {
  try {
    const binding = localStorage.getItem(bindingKey(tenant));
    return binding ? { [BINDING_HEADER]: binding } : {};
  } catch {
    return {};
  }
}

async function lookupSession(
  tenant: string,
): Promise<{ signedIn?: boolean; accessToken?: string }> {
  const response = await fetch(browserAuthPath({ tenant, action: 'token' }), {
    cache: 'no-store',
    headers: bindingHeaders(tenant),
  });
  if (!response.ok) throw new Error(`Session lookup failed with ${response.status}`);
  return response.json();
}

export async function isSignedIn(tenant: string): Promise<boolean> {
  try {
    return Boolean((await lookupSession(tenant)).signedIn);
  } catch {
    return false;
  }
}

// Propagate lookup failures; treating them as signed-out would prompt an authenticated guest for another code.
export async function fetchAuthorization(tenant: string): Promise<string | undefined> {
  const { accessToken } = await lookupSession(tenant);
  return accessToken ? `Bearer ${accessToken}` : undefined;
}

// Only the verification nonce and lifetime reach the form; state and PKCE stay server-side.
export type BrowserSignInAttempt =
  | { verificationId: string; expiresInSeconds: number; authorizeUrl?: never }
  | { authorizeUrl: string; verificationId?: never; expiresInSeconds?: never };

export async function startSignIn(tenant: string): Promise<BrowserSignInAttempt | undefined> {
  const response = await fetch(browserAuthPath({ tenant, action: 'signin' }), { method: 'POST' });
  if (!response.ok) return undefined;
  const { verificationId, expiresInSeconds, binding, authorizeUrl } = (await response.json()) as {
    verificationId?: string;
    expiresInSeconds?: number;
    binding?: string;
    authorizeUrl?: string;
  };
  if (authorizeUrl && URL.canParse(authorizeUrl)) {
    if (binding) rememberBinding({ tenant, binding });
    return { authorizeUrl };
  }
  // Without a lifetime, the form could keep accepting codes for an expired attempt.
  if (!verificationId || !Number.isFinite(expiresInSeconds)) return undefined;
  if (binding) rememberBinding({ tenant, binding });
  return { verificationId, expiresInSeconds: expiresInSeconds as number };
}

// The server reconstructs the callback; the browser supplies no destination.
export async function completeSignIn({
  tenant,
  verificationId,
}: {
  tenant: string;
  verificationId: string;
}): Promise<boolean> {
  const response = await fetch(browserAuthPath({ tenant, action: 'complete' }), {
    method: 'POST',
    headers: { ...bindingHeaders(tenant), 'Content-Type': 'application/json' },
    body: JSON.stringify({ verificationId }),
  });
  return response.ok;
}
